// 자가진단 프로젝트의 결과보고서 이름 규칙 (표지 제목·파일명).
// 소비처: report.naming.mixin.js, ReportBulkGenerator.vue
//
// 넥센 자가진단 프로젝트는 코드가 NEXTIER-SELF 로 시작한다 (NEXTIER-SELF_TEST 등). NEXTIER-S/M/L 은 서면평가라 제외.
// ponytail: 프로젝트 코드 접두사 하드코딩 — 넥센 밖에서 자가진단 프로젝트가 생기면 백엔드가 보고서 info 에
// self_assessment 를 실어 주는 쪽으로 옮긴다 (GEProjectCodes.self_assessment 와 프로젝트 편집 화면 토글은 이미 있다).
const SELF_ASSESSMENT_PROJECT_PREFIX = 'NEXTIER-SELF';

// 중문(ca)은 요청 범위 밖이라 라벨이 없다 — 표지는 그대로 두고, 파일명은 ko 라벨로 떨어진다.
const LABEL = { ko: '자가진단', en: 'Self-Assessment' };

function isSelfAssessmentProject (project_code) {
    return typeof project_code === 'string' && project_code.startsWith(SELF_ASSESSMENT_PROJECT_PREFIX);
}

// 표지 제목(줄 배열)에 "(자가진단)" 을 붙인다. ko 는 마지막 줄 뒤에 잇는다.
// en 은 한 줄에 이으면 582px 로 A4 폭(446px)을 넘어 잘리므로 별도 줄로 내린다 (NotoSansKR Bold 30 실측).
// 업체명 위치는 제목 높이로 계산되므로 줄이 늘어도 자동으로 밀린다 (report.supply.mixin.js).
// en 은 파일명과 용어를 맞춰 "ESG Evaluation" → "ESG Assessment" 로도 바꾼다 (2026-09-21 사용자 결정).
// ⛔ 자가진단 프로젝트에만 적용해야 하므로 공용 cover_title 의 en 줄은 건드리지 않고 여기서만 바꾼다 —
//    서면평가(NEXTIER-S/M/L)·일반 supply 영문 표지는 "ESG Evaluation" 그대로다.
function withSelfAssessmentCoverTitle (lines, lang) {
    const label = LABEL[lang];
    if (!label) return lines;
    if (lang === 'en') return [...lines.map(line => line.replace('ESG Evaluation', 'ESG Assessment')), `(${label})`];
    return [...lines.slice(0, -1), `${lines[lines.length - 1]} (${label})`];
}

// 파일명의 평가유형 자리("서면평가")를 자가진단 라벨로 바꾼다. 평가유형(DOC/OSIS)과 무관하게 고정.
// en 은 supply 기본 이름 전체를 영문으로 바꾼다 (뒤에 붙는 `_{기업명}` 같은 토큰은 그대로).
const FILENAME_KO_BASE = '2026 공급망 ESG 실사_서면평가 결과보고서';
const FILENAME_EN = '2026 Supply Chain ESG Assessment_Self-Assessment Report';
function withSelfAssessmentFilename (filename, lang) {
    if (lang === 'en' && filename.includes(FILENAME_KO_BASE)) return filename.replace(FILENAME_KO_BASE, FILENAME_EN);
    return filename.replace('서면평가', LABEL[lang] || LABEL.ko);
}

// 넥센 서면평가(NEXTIER-S/M/L …) — NEXTIER- 로 시작하되 자가진단이 아닌 프로젝트. 해외 협력사용 영문 보고서를 위해
// en 표지·파일명만 자가진단과 같은 용어(ESG Assessment)로 맞춘다 (2026-09-21 사용자 승인). ko/ca 는 그대로.
// 파일명은 ko 처럼 평가유형(DOC/OSIS)과 무관하게 '서면평가' 자리를 고정 — ko 동작을 바꾸지 않는 선에서 en 만 대응한다.
const NEXEN_PROJECT_PREFIX = 'NEXTIER-';
const FILENAME_EN_WRITTEN = '2026 Supply Chain ESG Assessment_On-Desk Assessment Report';

// 넥센 전체(서면평가 + 자가진단) — 코드 설명 문구를 업종 중립판으로 바꿀 때 쓴다 (supply-2026.js *Nexen 맵).
function isNexenProject (project_code) {
    return typeof project_code === 'string' && project_code.startsWith(NEXEN_PROJECT_PREFIX);
}
function isNexenWrittenProject (project_code) {
    return isNexenProject(project_code) && !isSelfAssessmentProject(project_code);
}
function withNexenWrittenCoverTitle (lines, lang) {
    return lang === 'en' ? lines.map(line => line.replace('ESG Evaluation', 'ESG Assessment')) : lines;
}
function withNexenWrittenFilename (filename, lang) {
    return lang === 'en' ? filename.replace(FILENAME_KO_BASE, FILENAME_EN_WRITTEN) : filename;
}

// supply 보고서 표지·파일명 진입점 — 프로젝트 종류에 따라 위 변환 중 하나를 고르고, 넥센이 아니면 그대로 돌려준다.
function supplyCoverTitle (lines, lang, project_code) {
    if (isSelfAssessmentProject(project_code)) return withSelfAssessmentCoverTitle(lines, lang);
    if (isNexenWrittenProject(project_code)) return withNexenWrittenCoverTitle(lines, lang);
    return lines;
}
function supplyFilename (filename, lang, project_code) {
    if (isSelfAssessmentProject(project_code)) return withSelfAssessmentFilename(filename, lang);
    if (isNexenWrittenProject(project_code)) return withNexenWrittenFilename(filename, lang);
    return filename;
}

module.exports = {
    isSelfAssessmentProject, withSelfAssessmentCoverTitle, withSelfAssessmentFilename,
    isNexenProject, isNexenWrittenProject, withNexenWrittenCoverTitle, withNexenWrittenFilename,
    supplyCoverTitle, supplyFilename,
};
